import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { LoadPanel } from 'devextreme-react/load-panel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { Button, ButtonTheme } from '@apply/components';
import { detectDeviceType } from '../helpers';
import './pdfiframeviewer.scss';

type PdfViewerOptions = {
    fitWidth?: boolean;
    page?: number;
    // A percentage. Ignored when fitWidth is set.
    zoom?: number;
    hideToolbar?: boolean;
};

type PdfIFrameViewerProps = {
    url: string;
    height: string;
    width: string;
    title: string;
    className?: string;
    viewerOptions?: PdfViewerOptions;
};

// PDF Open Parameters, which tell the built-in viewer how to present the document. These go in
// the fragment and never the query string - the urls are Azure Blob SAS urls whose signature
// covers the query, and a fragment is never sent to the server at all.
//
// Every viewer silently ignores the parameters it does not know, and no two agree on the set:
// Chrome/Edge (PDFium) handle view/toolbar/navpanes but not zoom=page-width, Firefox (pdf.js) is
// the other way around, so fit-to-width emits both and lets each take the one it understands.
// Safari honours neither, but iOS already takes the "Open PDF" fallback path below.
function buildViewerFragment(options?: PdfViewerOptions): string {
    if (!options) {
        return '';
    }

    const params: string[] = [];

    if (options.page) {
        params.push(`page=${options.page}`);
    }
    if (options.fitWidth) {
        params.push('view=FitH', 'zoom=page-width');
    } else if (options.zoom) {
        params.push(`zoom=${options.zoom}`);
    }
    if (options.hideToolbar) {
        params.push('toolbar=0', 'navpanes=0');
    }

    return params.length ? `#${params.join('&')}` : '';
}

// Renders a PDF using the browser's built-in viewer. The url is used verbatim - do not encode it.
//
// The file urls are Azure Blob SAS urls. If the SAS was minted without rscd=inline/rsct=application/pdf
// the blob comes back as an attachment and the browser downloads it instead of rendering it in the
// iframe. We can't add those parameters here (they are part of the SAS string-to-sign, so appending
// them invalidates the signature), so instead we fetch the bytes and re-serve them as an
// object url typed application/pdf - Content-Disposition does not apply to a blob: url. That
// requires the storage account to allow cross origin reads; when it doesn't, we fall back to
// pointing the iframe straight at the url.
//
// Mobile browsers do not render PDFs inline in an iframe at all (iOS Safari shows only the first
// page, Chrome on Android usually shows a blank frame or a download prompt), so on mobile - and if
// the iframe fails to load anywhere - we show an "Open PDF" prompt instead of an empty region
// where the document should be.
export default function PdfIFrameViewer({ url, height, width, title, className, viewerOptions }: PdfIFrameViewerProps) {
    const [canEmbed, set_canEmbed] = useState(true);
    const [showLoadingIndicator, set_showLoadingIndicator] = useState(true);
    const [iframeSrc, set_iframeSrc] = useState<string>(undefined);

    useEffect(() => {
        if (detectDeviceType() !== 'other') {
            set_canEmbed(false);
            set_showLoadingIndicator(false);
            return;
        }

        let cancelled = false;
        let objectUrl: string;

        fetch(url)
            .then((response) => (response.ok ? response.blob() : Promise.reject(new Error(`${response.status}`))))
            .then((blob) => {
                if (cancelled) {
                    return;
                }
                objectUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                set_iframeSrc(objectUrl);
            })
            .catch(() => {
                // Blocked by CORS, or the blob is gone/expired. Let the iframe try the url
                // directly - it still renders whenever the SAS already serves inline.
                if (!cancelled) {
                    set_iframeSrc(url);
                }
            });

        // onLoad does not fire in every case - notably when the browser decides to download
        // the PDF rather than render it - so make sure the (screen blocking) load panel always
        // goes away on its own.
        const timeoutId = setTimeout(() => set_showLoadingIndicator(false), 1000 * 10);

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [url]);

    function iframeLoaded() {
        set_showLoadingIndicator(false);
    }

    function iframeFailed() {
        set_canEmbed(false);
        set_showLoadingIndicator(false);
    }

    const openInNewTab = () => window.open(url, '_blank', 'noopener');

    if (!canEmbed) {
        return (
            <div
                className={classnames('pdf-iframe-viewer__fallback', className)}
                style={{ height, width }}
            >
                <FontAwesomeIcon
                    icon={faFilePdf}
                    className='pdf-iframe-viewer__fallback-icon'
                    aria-hidden='true'
                />
                <p className='pdf-iframe-viewer__fallback-text'>This document opens in a new tab on your device.</p>
                <Button
                    id={`${title}-open-pdf`}
                    key={`${title}-open-pdf`}
                    theme={ButtonTheme.Primary}
                    width='200px'
                    handleClick={openInNewTab}
                >
                    Open PDF
                </Button>
            </div>
        );
    }

    // Appended here rather than stored in state so iframeSrc stays the bare object url the
    // cleanup above revokes.
    const fragment = iframeSrc?.includes('#') ? '' : buildViewerFragment(viewerOptions);

    return (
        <>
            {iframeSrc && (
                <iframe
                    title={title}
                    src={`${iframeSrc}${fragment}`}
                    height={height}
                    width={width}
                    className={classnames('pdf-iframe-viewer__frame', className)}
                    onLoad={iframeLoaded}
                    onError={iframeFailed}
                />
            )}
            <LoadPanel
                hideOnOutsideClick={false}
                visible={showLoadingIndicator}
                message='Loading...'
            />
        </>
    );
}
